import { FAWithPrice, Tranch } from "@/types/types";

export function calculateActualExercised(tranch: Tranch | undefined) {
  return tranch?.exercised.map((item) => item.shares).reduce((prev, curr) => prev + curr, 0) || 0;
}

export function calculateOptionsVested(tranch: Tranch | undefined) {
  if (!tranch?.vestingDate) return 0;
  if (tranch?.dateOfExpiration && new Date(tranch?.dateOfExpiration) > new Date()) {
    if (tranch?.vestingDate && new Date(tranch?.vestingDate) <= new Date()) {
      const actualExercised = calculateActualExercised(tranch);
      const optionsVested = tranch?.options * (tranch?.vestingPercentage / 100) - actualExercised;
      return optionsVested;
    }
  }
  return 0;
}

export function calculateOptionsToBeVested(tranch: Tranch | undefined) {
  if (!tranch?.vestingDate) return 0;
  if (tranch?.dateOfExpiration && new Date(tranch?.dateOfExpiration) > new Date()) {
    if (new Date(tranch?.vestingDate) > new Date()) {
      return tranch?.options * (tranch?.vestingPercentage / 100);
    }
  }
  return 0;
}

export function calculateExpiredOptions(tranch: Tranch | undefined) {
  const actualExercised = calculateActualExercised(tranch);

  return tranch?.dateOfExpiration
    ? new Date(tranch?.dateOfExpiration) <= new Date()
      ? tranch?.options * (tranch?.vestingPercentage / 100) - actualExercised
      : 0
    : 0;
}

export function calculateTotalAmount(sar: FAWithPrice, tranch: Tranch | undefined) {
  const optionsVested = calculateOptionsVested(tranch);
  const optionsToBeVested = calculateOptionsToBeVested(tranch);

  return (optionsToBeVested + optionsVested) * sar.price;
}

export function calculateVestedGain(sar: FAWithPrice, tranch: Tranch | undefined) {
  const optionsVested = calculateOptionsVested(tranch);
  return (sar.price - (tranch?.costOrGrant ?? 0)) * optionsVested > 0
    ? (sar.price - (tranch?.costOrGrant ?? 0)) * optionsVested
    : 0;
}

export function calculateNonVestedGain(sar: FAWithPrice, tranch: Tranch | undefined) {
  const optionsToBeVested = calculateOptionsToBeVested(tranch);
  return (sar.price - (tranch?.costOrGrant ?? 0)) * optionsToBeVested > 0
    ? (sar.price - (tranch?.costOrGrant ?? 0)) * optionsToBeVested
    : 0;
}

export function calculateTotalExercisedForItem(tranch: Tranch | undefined) {
  return tranch?.exercised.map((item) => item.shares).reduce((prev, curr) => prev + curr, 0);
}

export function calculateTaxableAmount(sar: FAWithPrice, tranch: Tranch | undefined) {
  const vestedGain = calculateVestedGain(sar, tranch);
  const nonVestedGain = calculateNonVestedGain(sar, tranch);
  return vestedGain + nonVestedGain;
}

export function calculateTotalAmountForSymbol(sar: FAWithPrice, tranches: Tranch[]) {
  let sum = 0;
  tranches.forEach((item) => {
    sum = sum + calculateTotalAmount(sar, item);
  });
  return sum;
}

export function calculateVestedGainForSymbol(sar: FAWithPrice, tranches: Tranch[]) {
  let sum = 0;
  tranches.forEach((item) => {
    sum = sum + calculateVestedGain(sar, item);
  });
  return sum;
}

export function calculateNonVestedGainForSymbol(sar: FAWithPrice, tranches: Tranch[]) {
  let sum = 0;
  tranches.forEach((item) => {
    sum = sum + calculateNonVestedGain(sar, item);
  });
  return sum;
}

export function calculateTotalTaxableAmountForSymbol(sar: FAWithPrice, tranches: Tranch[]) {
  let sum = 0;
  tranches.forEach((item) => {
    sum = sum + calculateTaxableAmount(sar, item);
  });
  return sum;
}
